import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Banknote, Building2, CreditCard, Mail, Plus, Printer, ShoppingCart, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Panel, Empty, RowSearch, useQueryFilter } from "@/pages/shared";
import StaffPicker from "@/components/StaffPicker";
import { uid } from "@/lib/uid";
import InvoiceDoneDialog from "@/components/InvoiceDoneDialog";

const WALKIN = "__walkin__";

export default function LosseVerkopen() {
  const { t } = useLang();
  const { user } = useAuth();
  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [stays, setStays] = useState([]);
  const [articles, setArticles] = useState([]);
  const [openSale, setOpenSale] = useState(null);
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(null); // verkoop die nog afgedrukt of gemaild moet worden
  const [pick, setPick] = useState("");
  const [pickQ, setPickQ] = useState("");
  const [billing, setBilling] = useState("private");
  const [payment, setPayment] = useState("cash");
  const [lines, setLines] = useState([]);
  const [q, setQ] = useQueryFilter();
  const [busy, setBusy] = useState(false);
  const [newArt, setNewArt] = useState(null);
  const [staff, setStaff] = useState(null);
  const [level, setLevel] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, r, a, o] = await Promise.all([
        api.get("/sales"), api.get("/reservations?status=checked_in"), api.get("/articles"), api.get("/sales/open"),
      ]);
      setRows(s.data); setStays(r.data); setArticles(a.data.filter((x) => x.active));
      setOpenSale(o.data?.id ? o.data : null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadLevel = useCallback(async () => {
    try {
      setLevel((await api.get("/cash/level")).data);
    } catch (e) {
      console.debug("Kassastand niet opgehaald (geen rechten of geen kassa)", e?.message);
    }
  }, []);
  useEffect(() => { loadLevel(); }, [loadLevel]);

  const guest = useMemo(() => stays.find((s) => s.id === pick), [stays, pick]);
  const company = guest?.booker?.company;
  const hasCompany = !!(company?.on_account && company?.name);
  const totals = useMemo(() => lines.reduce((acc, l) => {
    const sub = (Number(l.qty) || 0) * (Number(l.unit_price) || 0);
    acc.sub += sub; acc.vat += sub * (Number(l.vat_rate) || 0) / 100;
    return acc;
  }, { sub: 0, vat: 0 }), [lines]);

  const reset = () => { setPick(""); setPickQ(""); setBilling("private"); setPayment("cash"); setLines([]); };

  const addLine = (articleId) => {
    const a = articles.find((x) => x.id === articleId);
    if (!a) return;
    setLines((ls) => [...ls, { _uid: uid(), article_id: a.id, code: a.code, description: a.name, qty: 1, unit_price: a.price, vat_rate: a.vat_rate, stock: a.stock, track: a.track_stock }]);
  };

  const billTo = () => {
    if (pick === WALKIN || !guest) return { name: t("walkin") };
    const b = guest.booker || {};
    if (billing === "company" && company) {
      return { name: company.name, street: company.street, house_number: company.house_number, postal_code: company.postal_code, city: company.city, country: company.country, vat_number: company.vat_number, email: b.email || "" };
    }
    return { name: guest.guest_name, street: b.street, house_number: b.house_number, postal_code: b.postal_code, city: b.city, country: b.country, email: b.email };
  };

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.post("/sales", {
        guest_id: pick === WALKIN || !pick ? null : guest?.guest_id,
        pitch_label: pick === WALKIN ? "" : guest?.pitch_label || "",
        billing, payment, bill_to: billTo(), staff_id: staff?.id || null,
        lines: lines.map((l) => ({ article_id: l.article_id, code: l.code, description: l.description, qty: Number(l.qty), unit_price: Number(l.unit_price), vat_rate: Number(l.vat_rate) })),
      });
      (res.data.warnings || []).forEach((w) => toast.warning(w));
      const mailTo = pick === WALKIN ? "" : (guest?.booker?.email || "");
      setOpen(false); reset(); await load(); await loadLevel();
      toast.success(`${t("created")} — ${res.data.number}`);
      // een verkoop is pas afgewerkt zodra de factuur afgedrukt of gemaild is
      setDone({ sale: res.data, email: mailTo });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const printSale = (sale) => {
    setTimeout(() => window.open(`/print-verkoop?sale=${sale.id}`, "_blank"), 50);
    setTimeout(load, 2500);
  };

  const emailSale = async (sale) => {
    if (!sale.guest_id) return toast.error(t("no_email_print"));
    try {
      const { data } = await api.post(`/sales/${sale.id}/email`);
      await load();
      toast.success(`${t("email_sent")} ${data.to}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const createArticle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/articles", { ...newArt, price: Number(newArt.price) || 0, vat_rate: Number(newArt.vat_rate) || 21 });
      setArticles((a) => [...a, data]);
      addLine(data.id);
      setNewArt(null);
      toast.success(t("created"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/sales/${id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const filteredStays = stays.filter((s) => {
    const n = pickQ.trim().toLowerCase();
    return !n || [s.guest_name, s.pitch_label].some((v) => (v || "").toLowerCase().includes(n));
  });
  const visible = rows.filter((s) => {
    const n = q.trim().toLowerCase();
    if (!n) return true;
    const hay = [s.guest_name, s.pitch_label, s.description, s.number, ...(s.lines || []).map((l) => l.description)]
      .filter(Boolean).join(" ").toLowerCase();
    // elk woord moet ergens voorkomen, volgorde maakt niet uit
    return n.split(/\s+/).every((tok) => hay.includes(tok));
  });

  const artVat = Number(newArt?.vat_rate) || 0;
  const artPrice = Number(newArt?.price) || 0;

  return (
    <Panel
      title={`${t("sales")} (${visible.length}${q ? `/${rows.length}` : ""})`}
      testid="sales-panel"
      action={
        <div className="flex items-center gap-2">
          <RowSearch value={q} onChange={setQ} placeholder={t("search_placeholder")} testid="sales-search" />
          <Button onClick={() => { reset(); setOpen(true); }} disabled={!!openSale} data-testid="add-sale-button"><Plus className="h-4 w-4 mr-1" />{t("new_sale")}</Button>
        </div>
      }
    >
      {level?.over && (
        <div className="mx-5 mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-rose-300 bg-rose-50 dark:bg-rose-950/30 px-4 py-3" data-testid="cash-level-banner">
          <Banknote className="h-4 w-4 text-rose-600 shrink-0" />
          <p className="text-sm text-rose-900 dark:text-rose-200">
            <span className="font-semibold">{t("cash_level_warning")}</span> — {money(level.in_till)} ({t("cash_max_limit")}: {money(level.max_cash)})
            <span className="block text-[12px] opacity-90" data-testid="cash-level-where">{t("cash_deposit_where")}</span>
          </p>
        </div>
      )}

      {openSale && (
        <div className="mx-5 mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="open-sale-banner">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-900 flex-1 min-w-[12rem]">
            <span className="font-semibold">{t("sale_open_title")} {openSale.number}</span> — {t("sale_open_hint")}
          </p>
          <Button size="sm" onClick={() => printSale(openSale)} data-testid="finish-open-sale"><Printer className="h-4 w-4 mr-1" />{t("print_invoice")}</Button>
          <Button size="sm" variant="outline" onClick={() => remove(openSale.id)} data-testid="cancel-open-sale">{t("cancel_sale")}</Button>
        </div>
      )}

      {visible.length === 0 ? <Empty /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("invoice_number")}</TableHead>
              <TableHead>{t("date")}</TableHead>
              <TableHead>{t("guest")}</TableHead>
              <TableHead>{t("pitch")}</TableHead>
              <TableHead>{t("description")}</TableHead>
              <TableHead>{t("sale_billing")}</TableHead>
              <TableHead className="text-right">{t("total")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((s) => (
              <TableRow key={s.id} data-testid={`sale-row-${s.id}`}>
                <TableCell className="font-semibold text-primary tabular-nums">{s.number || "—"}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{fmtDate(s.created_at)}</TableCell>
                <TableCell className="font-semibold text-foreground">{s.guest_name || "—"}</TableCell>
                <TableCell className="font-bold text-primary text-xs">{s.pitch_label || "—"}</TableCell>
                <TableCell className="text-sm">{(s.lines || []).map((l) => `${l.qty}× ${l.description}`).join(", ") || s.description}</TableCell>
                <TableCell className="text-xs">
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    {s.billing === "company" ? <Building2 className="h-3 w-3" /> : <UserRound className="h-3 w-3" />}
                    {t(s.billing === "company" ? "sale_company" : "sale_private")} · {t(s.payment === "cash" ? "pay_cash" : "pay_account")}
                  </span>
                </TableCell>
                <TableCell className="text-right font-semibold tabular-nums">{money(s.total)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => printSale(s)} title={t("print_invoice")} data-testid={`print-sale-${s.id}`}><Printer className="h-4 w-4" /></Button>
                  {s.guest_id && <Button variant="ghost" size="sm" onClick={() => emailSale(s)} title={t("email_invoice")} data-testid={`email-sale-${s.id}`}><Mail className={`h-4 w-4 ${s.emailed_at ? "text-emerald-600" : ""}`} /></Button>}
                  {!s.printed_at && <Button variant="ghost" size="sm" onClick={() => remove(s.id)} data-testid={`delete-sale-${s.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto cb-scroll" data-testid="sale-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ShoppingCart className="h-4 w-4" />{t("new_sale")}</DialogTitle></DialogHeader>

          <StaffPicker staff={staff} setStaff={setStaff} />

          {/* 1. Facturatiegegevens bovenaan */}
          <div className="space-y-3 rounded-xl border border-border p-4" data-testid="sale-billing-block">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{t("billing_details")}</p>
            <RowSearch value={pickQ} onChange={setPickQ} placeholder={t("search_stay")} testid="sale-stay-search" />
            <div className="max-h-44 overflow-auto cb-scroll rounded-lg border border-border divide-y divide-slate-100">
              <button onClick={() => setPick(WALKIN)} data-testid="sale-pick-walkin"
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${pick === WALKIN ? "bg-secondary" : "hover:bg-muted"}`}>
                <UserRound className="h-4 w-4 text-muted-foreground" />{t("walkin")}
              </button>
              {filteredStays.map((s) => (
                <button key={s.id} onClick={() => setPick(s.id)} data-testid={`sale-pick-${s.pitch_label}`}
                  className={`w-full text-left px-3 py-2 flex items-center gap-3 ${pick === s.id ? "bg-secondary" : "hover:bg-muted"}`}>
                  <span className="font-bold text-primary text-sm w-14">{s.pitch_label}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-foreground truncate">{s.guest_name}</span>
                    <span className="block text-[11px] text-muted-foreground">{fmtDate(s.arrival)} → {fmtDate(s.departure)} · {s.persons} {t("persons").toLowerCase()}</span>
                  </span>
                  {s.booker?.company?.on_account && <Building2 className="h-4 w-4 text-slate-400" />}
                </button>
              ))}
            </div>

            {pick && (
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-lg bg-muted/50 p-3 text-xs text-foreground/90" data-testid="sale-billto-preview">
                  <p className="font-display font-bold text-sm text-foreground">{billTo().name}</p>
                  {pick !== WALKIN && <>
                    <p>{[billTo().street, billTo().house_number].filter(Boolean).join(" ")}</p>
                    <p>{[billTo().postal_code, billTo().city].filter(Boolean).join(" ")} {billTo().country}</p>
                    {billTo().vat_number && <p className="mt-1">{t("setup_vat_number")}: {billTo().vat_number}</p>}
                    <p className="text-muted-foreground mt-1">{guest?.pitch_label} · {fmtDate(guest?.arrival)} → {fmtDate(guest?.departure)}</p>
                  </>}
                </div>
                <div className="space-y-3">
                  {hasCompany && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t("sale_billing")}</Label>
                      <div className="flex gap-2">
                        {[["private", UserRound], ["company", Building2]].map(([k, Icon]) => (
                          <button key={k} onClick={() => setBilling(k)} data-testid={`sale-billing-${k}`}
                            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${billing === k ? "border-primary bg-secondary" : "border-border hover:bg-muted"}`}>
                            <Icon className="h-4 w-4" />{t(k === "company" ? "sale_company" : "sale_private")}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("payment")}</Label>
                    <div className="flex gap-2">
                      {[["cash", Banknote, "pay_cash"], ["card", CreditCard, "pay_card"], ["account", UserRound, "pay_account"]].map(([k, Icon, key]) => (
                        <button key={k} onClick={() => setPayment(k)} disabled={k === "account" && pick === WALKIN} data-testid={`sale-payment-${k}`}
                          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm disabled:opacity-40 ${payment === k ? "border-primary bg-secondary" : "border-border hover:bg-muted"}`}>
                          <Icon className="h-4 w-4" />{t(key)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 2. Artikellijnen daaronder */}
          <div className="space-y-3 rounded-xl border border-border p-4" data-testid="sale-lines-block">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{t("articles_lines")}</p>
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">{t("select_article")}</Label>
                <Select value="" onValueChange={addLine}>
                  <SelectTrigger data-testid="sale-article-select"><SelectValue placeholder={t("select_article")} /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {articles.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.code} · {a.name} — {money(a.price)} ({a.vat_rate}%) {a.track_stock ? `· ${t("art_stock")}: ${a.stock}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isAdmin ? (
                <Button variant="outline" onClick={() => setNewArt({ code: "", name: "", category: "", unit: "stuk", price: "", vat_rate: 21, min_stock: 0, track_stock: true, active: true })} data-testid="sale-new-article"><Plus className="h-4 w-4 mr-1" />{t("new_article")}</Button>
              ) : <p className="text-[11px] text-muted-foreground pb-2">{t("ask_admin_article")}</p>}
            </div>

            {lines.length === 0 ? <p className="text-xs text-muted-foreground">{t("sale_pick_lines")}</p> : (
              <div className="space-y-2">
                {lines.map((l, i) => (
                  <div key={l._uid} className="grid grid-cols-[1fr_5rem_6rem_6rem_2rem] gap-2 items-center rounded-lg border border-border px-3 py-2" data-testid={`sale-line-${i}`}>
                    <span className="text-sm font-semibold text-foreground truncate">{l.code} · {l.description}
                      {l.track && Number(l.qty) > l.stock && <span className="ml-2 text-[11px] text-amber-600 font-semibold">{t("stock_negative_warning")}</span>}
                    </span>
                    <Input type="number" min="1" step="1" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} data-testid={`sale-qty-${i}`} />
                    <span className="text-sm tabular-nums text-muted-foreground">{money(l.unit_price)}</span>
                    <span className="text-sm font-semibold tabular-nums text-right">{money((Number(l.qty) || 0) * l.unit_price)}</span>
                    <Button variant="ghost" size="sm" onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`sale-del-line-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </div>
                ))}
                <div className="flex flex-col items-end gap-0.5 pt-1 text-sm">
                  <span className="text-muted-foreground">{t("subtotal")}: <span className="tabular-nums font-semibold text-foreground" data-testid="sale-subtotal">{money(totals.sub)}</span></span>
                  <span className="text-muted-foreground">{t("vat")}: <span className="tabular-nums font-semibold text-foreground">{money(totals.vat)}</span></span>
                  <span className="font-display font-extrabold text-xl text-primary tabular-nums" data-testid="sale-total">{money(totals.sub + totals.vat)}</span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
            {(!pick || lines.length === 0) && (
              <p className="text-xs text-amber-600 font-medium sm:mr-auto" data-testid="sale-blocked-reason">
                {!pick ? t("sale_pick_first") : t("sale_pick_lines")}
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
              <Button onClick={save} disabled={busy || !pick || lines.length === 0} data-testid="save-sale-button">
                {payment === "account" ? t("save_account") : payment === "card" ? `${t("save")} + ${t("pay_card")}` : t("save_cash")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Snel artikel bijmaken (admin) */}
      <Dialog open={!!newArt} onOpenChange={(o) => !o && setNewArt(null)}>
        <DialogContent data-testid="quick-article-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("new_article")}</DialogTitle></DialogHeader>
          {newArt && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">{t("art_code")}</Label><Input required value={newArt.code} onChange={(e) => setNewArt({ ...newArt, code: e.target.value.toUpperCase() })} data-testid="quick-article-code" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("art_category")}</Label><Input value={newArt.category} onChange={(e) => setNewArt({ ...newArt, category: e.target.value })} data-testid="quick-article-category" /></div>
              <div className="space-y-1 col-span-2"><Label className="text-xs">{t("art_name")}</Label><Input required value={newArt.name} onChange={(e) => setNewArt({ ...newArt, name: e.target.value })} data-testid="quick-article-name" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("unit_price")} ({t("excl_vat")})</Label><Input type="number" step="0.01" value={newArt.price} onChange={(e) => setNewArt({ ...newArt, price: e.target.value })} data-testid="quick-article-price" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("vat")} %</Label><Input type="number" step="1" value={newArt.vat_rate} onChange={(e) => setNewArt({ ...newArt, vat_rate: e.target.value })} data-testid="quick-article-vat" /></div>
              <div className="col-span-2 flex items-center justify-between rounded-lg bg-muted/60 px-3 py-2 text-sm">
                <span className="text-muted-foreground">{t("price_incl_vat")}</span>
                <span className="font-display font-extrabold text-primary tabular-nums" data-testid="quick-article-total">{money(artPrice * (1 + artVat / 100))}</span>
              </div>
              <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={newArt.track_stock} onChange={(e) => setNewArt({ ...newArt, track_stock: e.target.checked })} data-testid="quick-article-track" />{t("art_track_stock")}</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewArt(null)}>{t("cancel")}</Button>
            <Button onClick={createArticle} disabled={busy || !newArt?.code || !newArt?.name} data-testid="quick-article-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Een verkoop is pas afgewerkt zodra de factuur afgedrukt of gemaild is */}
      <InvoiceDoneDialog
        open={!!done}
        number={done?.sale?.number}
        email={done?.email}
        onFinish={async ({ print, mail }) => {
          if (print) printSale(done.sale);
          if (mail) await emailSale(done.sale);
          setDone(null);
          await load();
        }}
      />
    </Panel>
  );
}
